import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Calendar,
  Clock,
  MapPin,
  Users,
  Coffee,
  Beaker,
  Flame,
  Package,
  ArrowRight,
  ChevronDown,
  Gift,
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';

const EVENT = {
  name: 'The Bond Coffee Lab',
  tagline: 'Taste. Tinker. Roast.',
  date: 'Sunday, October 25, 2026',
  time: '10:00 AM–12:00 PM',
  duration: '2 hours',
  location: 'Bond Coffee, Marblehead, Massachusetts',
  capacity: '6 people',
  price: '$125',
};

const STEPS = [
  {
    icon: Coffee,
    number: '01',
    title: 'Taste',
    copy: 'Drink several different coffees side by side and get a feel for what you actually like.',
  },
  {
    icon: Beaker,
    number: '02',
    title: 'Tinker',
    copy: 'Experiment with different combinations and build a blend around your own taste.',
  },
  {
    icon: Flame,
    number: '03',
    title: 'Roast',
    copy: 'Roast coffee with us and see how it all comes together.',
  },
  {
    icon: Package,
    number: '04',
    title: 'Make It Yours',
    copy: 'Leave with a freshly roasted 12 oz bag created around the flavor profile you built.',
  },
];

const INCLUSIONS = [
  {
    icon: Package,
    title: 'Custom 12 oz Coffee',
    copy: 'Freshly roasted to your exact tastes — the blend you built, bagged and ready to go.',
  },
  {
    icon: Coffee,
    title: 'Bond Coffee Mug',
    copy: 'Your own Bond mug to drink from at home and remember the morning by.',
  },
  {
    icon: Gift,
    title: 'Bond Winter Hat',
    copy: 'A Bond winter hat to keep you warm through the season ahead.',
  },
];

const FAQS = [
  {
    q: 'Do I need to know anything about coffee?',
    a: 'No. Curiosity is enough.',
  },
  {
    q: 'Will we really roast coffee?',
    a: 'Yes. We’ll roast coffee together as part of the experience.',
  },
  {
    q: 'Do I create my own blend?',
    a: 'Yes. You’ll taste different coffees and build something around your preferences.',
  },
  {
    q: 'What do I take home?',
    a: 'A custom 12 oz bag of freshly roasted coffee, a Bond mug, and a Bond winter hat.',
  },
  {
    q: 'How many people will be there?',
    a: 'Only six.',
  },
  {
    q: 'Where does it happen?',
    a: 'At Bond Coffee in Marblehead, Massachusetts.',
  },
];

function BookButton({
  variant = 'primary',
  className = '',
}: {
  variant?: 'primary' | 'light';
  className?: string;
}) {
  const base =
    'group inline-flex items-center justify-center gap-3 rounded-full px-10 py-5 text-lg font-bold transition-all duration-300 transform hover:scale-[1.03]';
  const styles =
    variant === 'primary'
      ? 'bg-[#132947] text-white hover:bg-[#1a3a5f] shadow-xl shadow-[#132947]/20'
      : 'bg-[#fda36b] text-[#132947] hover:bg-[#fc9559] shadow-xl shadow-[#fda36b]/30';

  return (
    <Link to="/coffee-lab/book" className={`${base} ${styles} ${className}`}>
      Book Now
      <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
    </Link>
  );
}

function DetailChip({
  icon: Icon,
  label,
  value,
}: {
  icon: typeof Calendar;
  label: string;
  value: string;
}) {
  return (
    <div className="flex items-center gap-3">
      <div className="flex items-center justify-center w-10 h-10 rounded-full bg-[#132947]/5 border border-[#132947]/10 shrink-0">
        <Icon className="w-5 h-5 text-[#132947]" strokeWidth={1.5} />
      </div>
      <div className="text-left">
        <div className="text-xs uppercase tracking-wider text-[#132947]/50 font-semibold">
          {label}
        </div>
        <div className="text-sm font-medium text-[#132947]">{value}</div>
      </div>
    </div>
  );
}

export default function CoffeeLab() {
  useEffect(() => {
    document.title = 'The Bond Coffee Lab | Bond Coffee Marblehead';

    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute(
        'content',
        'Join Bond Coffee in Marblehead for a hands-on coffee experience. Taste coffees, build your own blend, roast with us, and leave with a custom 12 oz bag, a Bond mug, and a winter hat. October 25, 2026. Six spots only.'
      );
    }

    return () => {
      document.title = 'Coffee & Community | Bond Coffee × Marblehead Family Fund';
      if (metaDesc) {
        metaDesc.setAttribute(
          'content',
          'See how your Bond Coffee purchase supports the Marblehead Family Fund and helps create safe, inclusive and fun spaces for Marblehead families.'
        );
      }
    };
  }, []);

  return (
    <div className="min-h-screen bg-[#faf7f2]">
      <Header />

      {/* ───────────────────────── HERO ───────────────────────── */}
      <section className="relative bg-[#faf7f2] pt-12 pb-20 md:pt-16 md:pb-28 overflow-hidden">
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-[#fda36b]/8 rounded-full blur-[120px] pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-[#132947]/5 rounded-full blur-[100px] pointer-events-none" />

        <div className="relative max-w-6xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div className="text-center lg:text-left">
              <div className="inline-flex items-center gap-2 mb-6">
                <div className="h-px w-8 bg-[#fda36b]" />
                <span className="text-[#fda36b] text-xs font-bold uppercase tracking-[0.2em]">
                  A Bond Coffee Experience
                </span>
              </div>

              <h1 className="font-bold text-[#132947] leading-[1.05] tracking-tight text-5xl sm:text-6xl md:text-7xl">
                The Bond
                <br />
                Coffee Lab
              </h1>

              <p className="mt-6 text-2xl md:text-3xl font-light text-[#fda36b] tracking-wide">
                {EVENT.tagline}
              </p>

              <p className="mt-8 text-lg md:text-xl text-[#132947]/75 leading-relaxed max-w-xl mx-auto lg:mx-0">
                Come spend a morning inside Bond. Taste coffees from around the world,
                build a blend around what you actually like, roast with us, and leave
                with a coffee that’s yours.
              </p>

              <p className="mt-5 text-base md:text-lg text-[#132947]/55 leading-relaxed max-w-xl mx-auto lg:mx-0">
                We’ve had so much fun building Bond — tasting, roasting, and learning —
                that we wanted to open the door and share it with a small group of
                curious coffee people.
              </p>

              <div className="mt-10 grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-5 max-w-lg mx-auto lg:mx-0">
                <DetailChip icon={MapPin} label="Where" value="Marblehead, MA" />
                <DetailChip icon={Users} label="Capacity" value="6 spots" />
                <DetailChip icon={Coffee} label="Price" value="$125/person" />
              </div>

              <div className="mt-10 flex flex-col items-center lg:items-start gap-3">
                <BookButton />
                <p className="text-sm text-[#132947]/50 font-medium">
                  Just six seats around the table.
                </p>
              </div>
            </div>

            <div className="relative">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl shadow-[#132947]/15 aspect-[4/5]">
                <img
                  src="https://images.unsplash.com/photo-1447933601403-0c6688de566e?auto=format&fit=crop&w=900&q=80"
                  alt="Coffee being poured at a tasting table"
                  className="w-full h-full object-cover"
                  loading="eager"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#132947]/30 via-transparent to-transparent" />
              </div>

              <div className="absolute -bottom-6 -left-4 sm:-left-8 bg-white rounded-2xl shadow-xl px-6 py-5 border border-[#132947]/8">
                <div className="flex items-center gap-4">
                  <div className="flex items-center justify-center w-12 h-12 rounded-full bg-[#132947]">
                    <Users className="w-6 h-6 text-[#fda36b]" strokeWidth={1.5} />
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-[#132947] leading-none">6</div>
                    <div className="text-sm text-[#132947]/60 mt-1">seats only</div>
                  </div>
                </div>
              </div>

              <div className="absolute -top-4 -right-2 sm:-right-6 bg-[#fda36b] rounded-2xl shadow-xl px-5 py-4">
                <div className="text-3xl font-bold text-[#132947] leading-none">$125</div>
                <div className="text-xs text-[#132947]/70 mt-1 font-medium uppercase tracking-wider">
                  per person
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ───────────────────── WHAT THIS IS ───────────────────── */}
      <section className="bg-white py-20 md:py-28">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-[#132947] tracking-tight leading-tight">
            Less classroom.
            <br />
            More coffee table.
          </h2>
          <div className="w-16 h-1 bg-[#fda36b] mx-auto mt-8 rounded-full" />

          <div className="mt-10 space-y-6 text-lg md:text-xl text-[#132947]/70 leading-relaxed">
            <p>
              This is hands-on and conversational. No expertise is needed — just bring
              your curiosity. We’ll taste, compare, ask questions, blend, roast, and learn
              together.
            </p>
            <p>
              Whether you already love coffee or are simply curious about it, you’ll feel
              at home here. It’s fun, approachable, and genuinely valuable — not a lecture,
              not a trial class, just a good morning spent with good coffee.
            </p>
          </div>

          <div className="mt-12 inline-flex items-center gap-3 bg-[#132947]/5 border border-[#132947]/10 rounded-full px-6 py-3">
            <Coffee className="w-5 h-5 text-[#fda36b]" strokeWidth={1.5} />
            <span className="text-[#132947] font-medium text-base">
              Come make coffee with us.
            </span>
          </div>
        </div>
      </section>

      {/* ──────────────────── FOUR-STEP EXPERIENCE ──────────────────── */}
      <section className="bg-[#faf7f2] py-20 md:py-28">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-16">
            <span className="text-[#fda36b] text-xs font-bold uppercase tracking-[0.2em]">
              The Experience
            </span>
            <h2 className="mt-4 text-4xl md:text-5xl font-bold text-[#132947] tracking-tight">
              Four steps. One morning.
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {STEPS.map((step, i) => {
              const Icon = step.icon;
              return (
                <div
                  key={step.title}
                  className="group relative bg-white rounded-2xl p-8 border border-[#132947]/8 transition-all duration-300 hover:shadow-xl hover:shadow-[#132947]/8 hover:-translate-y-1"
                  style={{ animationDelay: `${i * 100}ms` }}
                >
                  <div className="absolute top-6 right-6 text-5xl font-bold text-[#132947]/8 select-none">
                    {step.number}
                  </div>
                  <div className="flex items-center justify-center w-14 h-14 rounded-2xl bg-[#132947] mb-6 group-hover:bg-[#1a3a5f] transition-colors">
                    <Icon className="w-7 h-7 text-[#fda36b]" strokeWidth={1.5} />
                  </div>
                  <h3 className="text-xl font-bold text-[#132947] mb-3 tracking-tight">
                    {step.title}
                  </h3>
                  <p className="text-[#132947]/65 leading-relaxed text-[15px]">
                    {step.copy}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ──────────────────── WHAT YOU TAKE HOME ──────────────────── */}
      <section className="bg-[#132947] py-20 md:py-28 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-[#fda36b]/10 rounded-full blur-[120px] pointer-events-none" />

        <div className="relative max-w-5xl mx-auto px-6">
          <div className="text-center mb-14">
            <h2 className="text-4xl md:text-5xl font-bold text-white tracking-tight">
              You’re not leaving empty-handed.
            </h2>
            <p className="mt-6 text-lg text-white/60 max-w-2xl mx-auto">
              Everything is included in your ticket — no extras, no upsells.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {INCLUSIONS.map((item) => {
              const Icon = item.icon;
              return (
                <div
                  key={item.title}
                  className="bg-white/5 border border-white/10 rounded-2xl p-8 backdrop-blur-sm hover:bg-white/8 hover:border-[#fda36b]/30 transition-all duration-300"
                >
                  <div className="flex items-center justify-center w-14 h-14 rounded-2xl bg-[#fda36b]/15 border border-[#fda36b]/20 mb-6">
                    <Icon className="w-7 h-7 text-[#fda36b]" strokeWidth={1.5} />
                  </div>
                  <h3 className="text-lg font-bold text-white mb-2">{item.title}</h3>
                  <p className="text-white/55 leading-relaxed text-sm">{item.copy}</p>
                </div>
              );
            })}
          </div>

          <div className="mt-12 text-center">
            <p className="text-lg text-white/70 leading-relaxed max-w-2xl mx-auto">
              Plus two hours of tasting, roasting, coffee conversation, and a much better
              understanding of what you actually like.
            </p>
          </div>

          <div className="mt-14 flex flex-col items-center gap-3">
            <BookButton variant="light" />
            <p className="text-sm text-white/40">
              Coffee, mug, and winter hat are all included.
            </p>
          </div>
        </div>
      </section>

      {/* ──────────────────── WHY WE'RE DOING THIS ──────────────────── */}
      <section className="bg-[#faf7f2] py-20 md:py-28">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <span className="text-[#fda36b] text-xs font-bold uppercase tracking-[0.2em]">
            Why We’re Doing This
          </span>

          <div className="mt-8 space-y-6 text-lg md:text-xl text-[#132947]/75 leading-relaxed">
            <p>
              Bond started with a simple belief: great coffee brings people together.
            </p>
            <p>
              As we’ve built Bond, we’ve spent a lot of time tasting, roasting,
              experimenting, and learning. We’ve loved that process so much that we want
              to open the door and share some of it.
            </p>
            <p>
              The Coffee Lab is a way to spend a morning with us, talk coffee, learn
              together, and make something that’s genuinely yours.
            </p>
          </div>

          <div className="mt-12 flex items-center justify-center gap-3">
            <div className="h-px w-12 bg-[#fda36b]/40" />
            <span className="text-[#132947]/50 font-medium italic">
              We’ll share what we know, and probably learn something too.
            </span>
            <div className="h-px w-12 bg-[#fda36b]/40" />
          </div>
        </div>
      </section>

      {/* ──────────────────── FINAL BOOKING SECTION ──────────────────── */}
      <section className="bg-white py-20 md:py-28">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-[#132947] tracking-tight">
              Save your seat.
            </h2>
            <p className="mt-4 text-lg text-[#132947]/60">
              Your coffee. Your bag. Your taste.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8">
            <div className="bg-[#faf7f2] rounded-2xl p-8 md:p-10 border border-[#132947]/8">
              <h3 className="text-sm font-bold uppercase tracking-[0.15em] text-[#fda36b] mb-8">
                Event Details
              </h3>
              <dl className="space-y-5">
                {[
                  ['Event', EVENT.name],
                  ['Date', EVENT.date],
                  ['Time', EVENT.time],
                  ['Duration', EVENT.duration],
                  ['Location', EVENT.location],
                  ['Capacity', EVENT.capacity],
                  ['Price', `${EVENT.price} per person`],
                ].map(([label, value]) => (
                  <div
                    key={label}
                    className="flex justify-between items-baseline gap-4 border-b border-[#132947]/8 pb-4 last:border-0 last:pb-0"
                  >
                    <dt className="text-[#132947]/50 text-sm font-medium uppercase tracking-wider">
                      {label}
                    </dt>
                    <dd className="text-[#132947] font-semibold text-right">{value}</dd>
                  </div>
                ))}
              </dl>
            </div>

            <div className="bg-[#132947] rounded-2xl p-8 md:p-10 flex flex-col">
              <h3 className="text-sm font-bold uppercase tracking-[0.15em] text-[#fda36b] mb-8">
                You’ll Leave With
              </h3>
              <ul className="space-y-4 mb-8">
                {[
                  'Custom 12 oz freshly roasted coffee',
                  'Bond mug',
                  'Bond winter hat',
                ].map((item) => (
                  <li key={item} className="flex items-center gap-3 text-white/85">
                    <div className="flex items-center justify-center w-6 h-6 rounded-full bg-[#fda36b]/20 shrink-0">
                      <Coffee className="w-3.5 h-3.5 text-[#fda36b]" strokeWidth={2} />
                    </div>
                    <span className="text-base">{item}</span>
                  </li>
                ))}
              </ul>

              <div className="mt-auto pt-6">
                <div className="flex items-baseline gap-2 mb-6">
                  <span className="text-4xl font-bold text-white">$125</span>
                  <span className="text-white/50 text-base">per person</span>
                </div>
                <BookButton variant="light" className="w-full" />
                <p className="mt-4 text-sm text-white/40 text-center">
                  Coffee, mug, and winter hat are all included.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ───────────────────────── FAQ ───────────────────────── */}
      <section className="bg-[#faf7f2] py-20 md:py-28">
        <div className="max-w-3xl mx-auto px-6">
          <div className="text-center mb-12">
            <span className="text-[#fda36b] text-xs font-bold uppercase tracking-[0.2em]">
              Questions
            </span>
            <h2 className="mt-4 text-4xl md:text-5xl font-bold text-[#132947] tracking-tight">
              Good questions.
            </h2>
          </div>

          <div className="space-y-3">
            {FAQS.map((faq, i) => (
              <details
                key={i}
                className="group bg-white rounded-xl border border-[#132947]/8 overflow-hidden"
              >
                <summary className="flex items-center justify-between cursor-pointer px-6 py-5 list-none select-none">
                  <span className="text-lg font-semibold text-[#132947]">{faq.q}</span>
                  <ChevronDown
                    className="w-5 h-5 text-[#fda36b] shrink-0 transition-transform duration-300 group-open:rotate-180"
                    strokeWidth={1.5}
                  />
                </summary>
                <div className="px-6 pb-5 text-[#132947]/65 leading-relaxed">
                  {faq.a}
                </div>
              </details>
            ))}
          </div>

          <div className="mt-16 text-center">
            <p className="text-xl text-[#132947]/70 mb-6">
              Six seats. One morning. A coffee that’s yours.
            </p>
            <BookButton />
            <p className="mt-4 text-sm text-[#132947]/50">
              Just six seats around the table.
            </p>
          </div>
        </div>
      </section>

      {/* back-home strip */}
      <section className="bg-white border-t border-[#132947]/8 py-12">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-[#132947]/60 hover:text-[#132947] transition-colors font-medium"
          >
            <ArrowRight className="w-4 h-4 rotate-180" />
            Back to Bond Coffee
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
